
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {TradingCentralLanguage} from '../core/TradingCentralLanguage';
import {FieldModelTradingCentralLanguage} from '../core/TradingCentralLanguage';

/**
 * TradingCentralArticleQuery struct
 */
class TradingCentralArticleQuery {
  /**
   * Initialize struct
   * @param {!string=} provider
   * @param {!string=} articleId
   * @param {!TradingCentralLanguage=} language
   * @constructor
   */
  constructor (provider = '', articleId = '', language = new TradingCentralLanguage(TradingCentralLanguage.EN)) {
    this.Provider = provider
    this.ArticleId = articleId
    this.Language = language
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradingCentralArticleQuery}
   * @param {!TradingCentralArticleQuery} other Other struct
   * @returns {!TradingCentralArticleQuery} This struct
   */
  copy (other) {
    if (other.Provider != null) {
      this.Provider = other.Provider
    } else {
      this.Provider = null
    }
    if (other.ArticleId != null) {
      this.ArticleId = other.ArticleId
    } else {
      this.ArticleId = null
    }
    if (other.Language != null) {
      this.Language = TradingCentralLanguage.fromObject(other.Language)
    } else {
      this.Language = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradingCentralArticleQuery}
   * @returns {!TradingCentralArticleQuery} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradingCentralArticleQueryModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradingCentralArticleQueryModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradingCentralArticleQuery}
   * @param {!TradingCentralArticleQuery} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradingCentralArticleQuery)) {
      throw new TypeError('Instance of TradingCentralArticleQuery is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradingCentralArticleQuery}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Provider: ((this.Provider != null) ? this.Provider : null),
      ArticleId: ((this.ArticleId != null) ? this.ArticleId : null),
      Language: ((this.Language != null) ? this.Language : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradingCentralArticleQuery.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradingCentralArticleQuery} other Object value
   * @returns {!TradingCentralArticleQuery} Created struct
   */
  static fromObject (other) {
    return new TradingCentralArticleQuery().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradingCentralArticleQuery}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradingCentralArticleQuery.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradingCentralArticleQuery}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5213
  }
}

export { TradingCentralArticleQuery };

/**
 * Fast Binary Encoding TradingCentralArticleQuery field model
 */
class FieldModelTradingCentralArticleQuery extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Provider = new fbe.FieldModelString(buffer, 4 + 4)
    this.ArticleId = new fbe.FieldModelString(buffer, this.Provider.fbeOffset + this.Provider.fbeSize)
    this.Language = new FieldModelTradingCentralLanguage(buffer, this.ArticleId.fbeOffset + this.ArticleId.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradingCentralArticleQuery}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradingCentralArticleQuery}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Provider.fbeSize + this.ArticleId.fbeSize + this.Language.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradingCentralArticleQuery}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Provider.fbeExtra + this.ArticleId.fbeExtra + this.Language.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradingCentralArticleQuery}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradingCentralArticleQuery.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradingCentralArticleQuery}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5213
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradingCentralArticleQuery}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradingCentralArticleQuery}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Provider.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Provider.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Provider.fbeSize

    if ((fbeCurrentSize + this.ArticleId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ArticleId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ArticleId.fbeSize

    if ((fbeCurrentSize + this.Language.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Language.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Language.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradingCentralArticleQuery}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradingCentralArticleQuery}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradingCentralArticleQuery}
   * @param {!TradingCentralArticleQuery} fbeValue Default value, defaults is new TradingCentralArticleQuery()
   * @returns {!TradingCentralArticleQuery} TradingCentralArticleQuery value
   */
  get (fbeValue = new TradingCentralArticleQuery()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradingCentralArticleQuery}
   * @param {!TradingCentralArticleQuery} fbeValue TradingCentralArticleQuery value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Provider.fbeSize) <= fbeStructSize) {
      fbeValue.Provider = this.Provider.get()
    } else {
      fbeValue.Provider = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Provider.fbeSize

    if ((fbeCurrentSize + this.ArticleId.fbeSize) <= fbeStructSize) {
      fbeValue.ArticleId = this.ArticleId.get()
    } else {
      fbeValue.ArticleId = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ArticleId.fbeSize

    if ((fbeCurrentSize + this.Language.fbeSize) <= fbeStructSize) {
      fbeValue.Language = this.Language.get(new TradingCentralLanguage(TradingCentralLanguage.EN))
    } else {
      fbeValue.Language = new TradingCentralLanguage(TradingCentralLanguage.EN)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Language.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradingCentralArticleQuery}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradingCentralArticleQuery}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradingCentralArticleQuery}
   * @param {!TradingCentralArticleQuery} fbeValue TradingCentralArticleQuery value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradingCentralArticleQuery}
   * @param {TradingCentralArticleQuery} fbeValue TradingCentralArticleQuery value
   */
  setFields (fbeValue) {
    this.Provider.set(fbeValue.Provider)
    this.ArticleId.set(fbeValue.ArticleId)
    this.Language.set(fbeValue.Language)
  }
}

export { FieldModelTradingCentralArticleQuery };

/**
 * Fast Binary Encoding TradingCentralArticleQuery model
 */
class TradingCentralArticleQueryModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradingCentralArticleQuery(this.buffer, 4)
  }

  /**
   * Get the TradingCentralArticleQuery model
   * @this {!TradingCentralArticleQueryModel}
   * @returns {!FieldModelTradingCentralArticleQuery} model TradingCentralArticleQuery model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradingCentralArticleQueryModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradingCentralArticleQueryModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradingCentralArticleQueryModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradingCentralArticleQueryModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradingCentralArticleQuery.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradingCentralArticleQueryModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradingCentralArticleQueryModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradingCentralArticleQueryModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradingCentralArticleQueryModel}
   * @param {!TradingCentralArticleQuery} value TradingCentralArticleQuery value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradingCentralArticleQueryModel}
   * @param {!TradingCentralArticleQuery} value TradingCentralArticleQuery value, defaults is new TradingCentralArticleQuery()
   * @return {!object} Deserialized TradingCentralArticleQuery value and its size
   */
  deserialize (value = new TradingCentralArticleQuery()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradingCentralArticleQuery(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradingCentralArticleQuery(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradingCentralArticleQueryModel}
   * @param {!number} prev Previous TradingCentralArticleQuery model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradingCentralArticleQueryModel };
